import {
  ContentsTabGroupEnum,
  ContentsTabIdEnum,
  IContentsConfig,
  IContentsSummaryCopy,
  ICreateSendContentsState,
  TContentsFilter,
} from '../types/createSendContents';
import { CreateSendMethodEnum, TCreateSendMethod } from '../types/createSendFlow';

export enum ContentsCardKindEnum {
  Kit = 'kit',
  Item = 'item',
}

export enum ContentsSourceEnum {
  IeCatalog = 'ie-catalog',
  Marketplace = 'marketplace',
}

export const CATALOG_LOAD_MORE_ERROR_MESSAGE = "We couldn't load more items";
export const CATALOG_ITEMS_ERROR_MESSAGE = "We couldn't load your inventory";
export const CATALOG_KITS_ERROR_MESSAGE = "We couldn't load your kits";

export const CONTENTS_FILTER_LABEL: Record<TContentsFilter, string> = {
  'your-inventory': 'Your inventory',
  department: 'Department',
  warehouse: 'Warehouse',
  backorder: 'Backorder',
  category: 'Category',
};

export const CONTENTS_FILTER_MENU_WIDTH: Partial<Record<TContentsFilter, number>> = {
  department: 180,
  warehouse: 180,
  backorder: 193,
  category: 220,
};

export const CONTENTS_SCOPE_LABEL: Record<ContentsCardKindEnum, string> = {
  [ContentsCardKindEnum.Kit]: 'Your kits',
  [ContentsCardKindEnum.Item]: CONTENTS_FILTER_LABEL['your-inventory'],
};

const BUDGET_EMPTY_SUFFIX = ' Or turn on Recipient budget below and let recipients choose for themselves.';

// "Choose a kit": the sender picks a ready-made kit or a single marketplace product.
const CURATED_EMPTY_SUMMARY: IContentsSummaryCopy = {
  emptyTitle: 'No items yet',
  emptyBody: `Pick a kit or marketplace item to start your send.${BUDGET_EMPTY_SUFFIX}`,
};

// "Build a kit": the sender assembles one from individual items, so "pick a kit" would misread.
const BUILD_EMPTY_SUMMARY: IContentsSummaryCopy = {
  emptyTitle: 'No items yet',
  emptyBody: `Add marketplace or inventory items to build your kit.${BUDGET_EMPTY_SUFFIX}`,
};

const RECIPIENTS_LINK_CONFIG: IContentsConfig = {
  tabs: [
    {
      id: ContentsTabIdEnum.Curated,
      label: 'Choose a kit',
      cardKind: ContentsCardKindEnum.Kit,
      searchPlaceholder: 'Search catalog...',
      filters: ['your-inventory', 'department', 'warehouse'],
      summary: CURATED_EMPTY_SUMMARY,
    },
    {
      id: ContentsTabIdEnum.Build,
      label: 'Build a kit',
      cardKind: ContentsCardKindEnum.Item,
      searchPlaceholder: 'Search by name or SKU...',
      filters: ['your-inventory', 'department', 'warehouse', 'backorder'],
      summary: BUILD_EMPTY_SUMMARY,
    },
  ],
  clearCartOnTabSwitch: true,
  tabGroup: ContentsTabGroupEnum.RecipientsLink,
};

const LOCATION_SUMMARY: IContentsSummaryCopy = {
  emptyTitle: 'No items selected',
  // No marketplace scope and no budget panel on Location, so neither is offered here.
  emptyBody: 'Add inventory items to build your kit.',
};

const LOCATION_CONFIG: IContentsConfig = {
  tabs: [
    {
      id: ContentsTabIdEnum.Curated,
      label: 'Curated kits',
      cardKind: ContentsCardKindEnum.Kit,
      searchPlaceholder: 'Search kits...',
      filters: ['your-inventory', 'department', 'warehouse'],
      summary: LOCATION_SUMMARY,
    },
    {
      id: ContentsTabIdEnum.Build,
      label: 'Individual items',
      cardKind: ContentsCardKindEnum.Item,
      searchPlaceholder: 'Search by name or SKU...',
      filters: ['your-inventory', 'department', 'warehouse', 'backorder'],
      summary: LOCATION_SUMMARY,
    },
  ],
  clearCartOnTabSwitch: false,
  tabGroup: ContentsTabGroupEnum.Location,
};

export const CREATE_SEND_CONTENTS_CONFIG: Record<TCreateSendMethod, IContentsConfig> = {
  [CreateSendMethodEnum.Recipients]: RECIPIENTS_LINK_CONFIG,
  [CreateSendMethodEnum.Link]: RECIPIENTS_LINK_CONFIG,
  [CreateSendMethodEnum.Location]: LOCATION_CONFIG,
};

export const DEFAULT_CREATE_SEND_CONTENTS_STATE: ICreateSendContentsState = {
  activeTab: {},
  kits: null,
  items: [],
  boxId: '',
  giftBudget: null,
  isGiftBudgetPending: false,
  pygOptionOrder: [],
};

export const CONTENTS_TOOLBAR_CONTAINER_NAME = 'catalog-toolbar';

export const CONTENTS_SCOPE_STACK_MAX_WIDTH: Record<ContentsSourceEnum, number> = {
  [ContentsSourceEnum.IeCatalog]: 520,
  [ContentsSourceEnum.Marketplace]: 280,
};
